// The app-icon language — how another product's mark sits on an innernet surface.
//
// Two decisions, and everything else follows from them.
//
// 1. THE SHAPE IS A SUPERELLIPSE, not a rounded rectangle. `border-radius: 27.5%`
//    (what we used before) makes a rect with four circular corners: the curve's
//    curvature jumps discontinuously where the arc meets the straight edge, and
//    the eye reads that break as hard. A superellipse — |2x-1|ⁿ + |2y-1|ⁿ = 1 —
//    has continuous curvature all the way round, which is why iOS icons feel
//    softer than their radius suggests. We use n = 4.2, a little rounder than
//    Apple's ~5, because innernet is warmer than a system icon.
//
// 2. THE TILE CARRIES THE BRAND'S OWN GROUND. A row of white squares with
//    coloured glyphs reads as a spreadsheet. A row where Claude sits on its
//    coral, Codex on black, Notion on white, Zed on near-black — that reads as
//    a shelf of real apps, which is the point: these are the places your memory
//    already lives.
//
// The marks themselves come from components/BrandIcon (the one registry). This
// file only decides the ground each one stands on and how much room it gets.

/** Superellipse (n = 4.2) in objectBoundingBox units — scales to any tile. */
export const SQUIRCLE = {
  clipId: 'innernet-squircle',
  bbox:
    'M1,0.5C1,0.5632 0.9993,0.6458 0.998,0.6896C0.9966,0.7334 0.9946,0.7415 0.9918,0.7627C0.9891,0.7838 0.9857,0.8003 0.9815,0.8165C0.9773,0.8326 0.9725,0.8464 0.9669,0.8594C0.9613,0.8725 0.955,0.884 0.9478,0.8948C0.9407,0.9055 0.9328,0.9151 0.9239,0.9239C0.9151,0.9328 0.9055,0.9407 0.8948,0.9478C0.884,0.955 0.8725,0.9613 0.8594,0.9669C0.8464,0.9725 0.8326,0.9773 0.8165,0.9815C0.8003,0.9857 0.7838,0.9891 0.7627,0.9918C0.7415,0.9946 0.7334,0.9966 0.6896,0.998C0.6458,0.9993 0.5632,1 0.5,1C0.4368,1 0.3542,0.9993 0.3104,0.998C0.2666,0.9966 0.2585,0.9946 0.2373,0.9918C0.2162,0.9891 0.1997,0.9857 0.1835,0.9815C0.1674,0.9773 0.1536,0.9725 0.1406,0.9669C0.1275,0.9613 0.116,0.955 0.1052,0.9478C0.0945,0.9407 0.0849,0.9328 0.0761,0.9239C0.0672,0.9151 0.0593,0.9055 0.0522,0.8948C0.045,0.884 0.0387,0.8725 0.0331,0.8594C0.0275,0.8464 0.0227,0.8326 0.0185,0.8165C0.0143,0.8003 0.0109,0.7838 0.0082,0.7627C0.0054,0.7415 0.0034,0.7334 0.002,0.6896C0.0007,0.6458 0,0.5632 0,0.5C0,0.4368 0.0007,0.3542 0.002,0.3104C0.0034,0.2666 0.0054,0.2585 0.0082,0.2373C0.0109,0.2162 0.0143,0.1997 0.0185,0.1835C0.0227,0.1674 0.0275,0.1536 0.0331,0.1406C0.0387,0.1275 0.045,0.116 0.0522,0.1052C0.0593,0.0945 0.0672,0.0849 0.0761,0.0761C0.0849,0.0672 0.0945,0.0593 0.1052,0.0522C0.116,0.045 0.1275,0.0387 0.1406,0.0331C0.1536,0.0275 0.1674,0.0227 0.1835,0.0185C0.1997,0.0143 0.2162,0.0109 0.2373,0.0082C0.2585,0.0054 0.2666,0.0034 0.3104,0.002C0.3542,0.0007 0.4368,0 0.5,0C0.5632,0 0.6458,0.0007 0.6896,0.002C0.7334,0.0034 0.7415,0.0054 0.7627,0.0082C0.7838,0.0109 0.8003,0.0143 0.8165,0.0185C0.8326,0.0227 0.8464,0.0275 0.8594,0.0331C0.8725,0.0387 0.884,0.045 0.8948,0.0522C0.9055,0.0593 0.9151,0.0672 0.9239,0.0761C0.9328,0.0849 0.9407,0.0945 0.9478,0.1052C0.955,0.116 0.9613,0.1275 0.9669,0.1406C0.9725,0.1536 0.9773,0.1674 0.9815,0.1835C0.9857,0.1997 0.9891,0.2162 0.9918,0.2373C0.9946,0.2585 0.9966,0.2666 0.998,0.3104C0.9993,0.3542 1,0.4368 1,0.5Z',
} as const

export interface AppIconSkin {
  /** the tile's ground */
  bg: string
  /** the mark's colour ON that ground */
  ink: string
  /**
   * Force every path in the mark to `ink`. Only for SILHOUETTE marks — a
   * multi-path or gradient logo (chrome, gemini, google) loses its identity,
   * and an OUTLINE glyph (codex) loses the counter that makes it readable.
   */
  force?: boolean
  /** mark size as a fraction of the tile. Default 0.54 — generous padding. */
  scale?: number
  /** a hairline for tiles whose ground matches the page (white on cream). */
  edge?: boolean
}

/**
 * Per-brand skins. Each ground is the brand's own — pulled from its real app
 * icon, not invented — so the row reads as a shelf of apps rather than a
 * uniform sprite sheet.
 */
export const APP_SKIN: Record<string, AppIconSkin> = {
  // ── marks that come with their own ground ──
  claude: { bg: '#D97757', ink: '#FFFFFF', force: true, scale: 0.56 },
  codex: { bg: '#000000', ink: '#FFFFFF', scale: 0.5 }, // outline glyph — never forced
  cursor: { bg: '#0D0D0D', ink: '#FFFFFF', force: true, scale: 0.5 },
  zed: { bg: '#18181B', ink: '#4C8DFF', scale: 0.54 },
  ollama: { bg: '#101014', ink: '#FFFFFF', force: true, scale: 0.5 },
  github: { bg: '#1B1F24', ink: '#FFFFFF', force: true, scale: 0.54 },
  obsidian: { bg: '#2B2235', ink: '#A78BFA', force: true, scale: 0.54 },
  linear: { bg: '#5E6AD2', ink: '#FFFFFF', force: true, scale: 0.52 },
  slack: { bg: '#4A154B', ink: '#FFFFFF', force: true, scale: 0.52 },
  perplexity: { bg: '#20808D', ink: '#FFFFFF', force: true, scale: 0.54 },
  fireworks: { bg: '#1B1033', ink: '#A46BFF', scale: 0.54 },
  cline: { bg: '#4B3FD1', ink: '#FFFFFF', force: true, scale: 0.5 },
  windsurf: { bg: '#0B100F', ink: '#58E6A6', force: true, scale: 0.54 },
  continue: { bg: '#121212', ink: '#FFFFFF', force: true, scale: 0.54 },

  // ── marks that keep their own colour, on white ──
  openai: { bg: '#FFFFFF', ink: '#0D0D0D', force: true, scale: 0.56, edge: true },
  notion: { bg: '#FFFFFF', ink: '#111111', force: true, scale: 0.56, edge: true },
  gemini: { bg: '#FFFFFF', ink: '#1C1C1E', scale: 0.54, edge: true }, // gradient mark
  google: { bg: '#FFFFFF', ink: '#1C1C1E', scale: 0.52, edge: true }, // 4-colour mark
  chrome: { bg: '#FFFFFF', ink: '#1C1C1E', scale: 0.58, edge: true }, // 4-colour mark
  gmail: { bg: '#FFFFFF', ink: '#1C1C1E', scale: 0.62, edge: true }, // 4-colour mark
  figma: { bg: '#FFFFFF', ink: '#1C1C1E', scale: 0.5, edge: true }, // 5-colour mark
  deepseek: { bg: '#FFFFFF', ink: '#4D6BFE', scale: 0.58, edge: true },
  antigravity: { bg: '#FFFFFF', ink: '#1C1C1E', force: true, scale: 0.54, edge: true },
  googledocs: { bg: '#FFFFFF', ink: '#4285F4', force: true, scale: 0.52, edge: true },
  // NOT forced: safari's compass is a STROKED ring on an unfilled svg, and
  // `fill: currentColor !important` fills the ring solid — a blue disc.
  safari: { bg: '#FFFFFF', ink: '#1B8CF3', scale: 0.58, edge: true },
  arc: { bg: '#FFFFFF', ink: '#1C1C1E', force: true, scale: 0.54, edge: true },
  edge: { bg: '#FFFFFF', ink: '#0F7FBF', force: true, scale: 0.56, edge: true },
  brave: { bg: '#FB542B', ink: '#FFFFFF', force: true, scale: 0.54 },
  x: { bg: '#000000', ink: '#FFFFFF', force: true, scale: 0.48 },
  cli: { bg: '#141414', ink: '#E8E8E8', scale: 0.5 },
  web: { bg: '#FFFFFF', ink: '#1C1C1E', scale: 0.54, edge: true },

  // innernet's own mark self-tints from the live organism hue.
  innernet: { bg: '#FFFFFF', ink: 'inherit', scale: 0.58, edge: true },
}

/** The skin for a brand key, with a neutral fallback that still looks made. */
export function skinFor(key: string): AppIconSkin {
  return APP_SKIN[key] ?? { bg: '#FFFFFF', ink: '#1C1C1E', scale: 0.54, edge: true }
}
